import { components } from "@certn/api/generated-schemas/client-portal";
import {
  StatusTag,
  StatusTagType,
} from "@certn/cascadia-react/components/tags/StatusTag";
import { TagSize } from "@certn/cascadia-react/components/tags/_Tag";
import { CascadiaColor } from "packages/cascadia-tokens";

export function renderStatusTag({
  status,
  decision,
  score,
  hasPopover = false,
  size,
  isScoreHidden = false,
}: {
  status: components["schemas"]["ClientPortalOverallStatus"];
  decision?: components["schemas"]["ClientPortalDecision"] | null;
  score?: components["schemas"]["ClientPortalOverallScore"] | null;
  hasPopover?: boolean;
  size?: TagSize;
  isScoreHidden?: boolean;
}): React.ReactElement {
  const isCompleteWithHiddenScore = status.name === "COMPLETE" && isScoreHidden;

  const tagProps = isCompleteWithHiddenScore
    ? getNeutralCompleteTagProps()
    : getStatusTagProps(status.name, decision?.name, score?.name);

  let label = status.label;
  if (status.name === "COMPLETE") {
    if (decision?.label) {
      label = `${status.label}: ${decision.label}`;
    } else if (score?.label && !isScoreHidden) {
      label = `${status.label}: ${score.label}`;
    }
  }

  return (
    <StatusTag
      isButton={hasPopover}
      statusInfoIcon={hasPopover}
      size={size}
      {...(tagProps as StatusTagType)}
    >
      {label}
    </StatusTag>
  );
}

function getNeutralCompleteTagProps() {
  return {
    leftIcon: "inventory",
    fill: "background-accent-blue-weak",
    color: "text-accent-blue-strong",
  };
}

function getStatusTagProps(
  status: string,
  decision: string | undefined,
  score: string | undefined
) {
  switch (status) {
    case "CASE_ORDERED":
    case "APPLICANT_INVITED":
    case "APPLICANT_OPENED":
    default:
      return {
        leftIcon: "pending",
        fill: "background-accent-neutral-medium",
        color: "text-base",
      };
    case "DRAFT":
      return {
        leftIcon: "edit",
        fill: "background-accent-neutral-medium",
        color: "text-base",
      };
    case "APPLICANT_STARTED":
      return {
        leftIcon: "edit_square",
        fill: "background-accent-neutral-medium",
        color: "text-base",
      };
    case "APPLICANT_SUBMITTED":
      return {
        leftIcon: "inventory",
        fill: "background-accent-neutral-medium",
        color: "text-base",
      };
    case "APPLICANT_EXPIRED":
    case "APPLICANT_DECLINED":
    case "INVITE_UNDELIVERABLE":
      return {
        leftIcon: "block",
        fill: "background-accent-red-weak",
        color: "text-accent-red-strong",
      };
    case "CLIENT_ACTION_REQUIRED":
      return {
        leftIcon: "emoji_flags",
        fill: "background-accent-red-weak",
        color: "text-accent-red-strong",
      };
    case "CASE_IN_PROGRESS":
      return {
        leftIcon: "document_scanner",
        fill: "background-accent-tofino-weak",
        color: "text-accent-tofino-strong",
      };
    case "APPLICANT_ACTION_REQUIRED":
      return {
        leftIcon: "emoji_flags",
        fill: "background-accent-neutral-medium",
        color: "text-base",
      };
    case "IN_DISPUTE":
      return {
        leftIcon: "hourglass_top",
        fill: "background-accent-purple-weak",
        color: "text-accent-purple-strong",
      };
    case "CANCELLED":
      return {
        leftIcon: "block",
        fill: "background-accent-red-weak",
        color: "text-accent-red-strong",
      };
    case "COMPLETE":
      return (
        getDecisionTagProps(decision) ||
        getScoreTagProps(score) || {
          leftIcon: "inventory",
          fill: "background-accent-green-weak",
          color: "text-accent-green-strong",
        }
      );
  }
}

function getDecisionTagProps(decision: string | undefined) {
  if (!decision) return;

  switch (decision) {
    case "MEETS_CRITERIA":
      return {
        leftIcon: "inventory",
        fill: "background-accent-green-weak",
        color: "text-accent-green-strong",
      };
    case "CRITERIA_NOT_MET":
    default:
      return {
        leftIcon: "quick_reference_all",
        fill: "background-accent-neutral-medium",
        color: "text-base",
      };
  }
}

function getScoreTagProps(score: string | undefined) {
  if (!score) return;

  switch (score) {
    case "CLEAR":
      return {
        leftIcon: "inventory",
        fill: "background-accent-blue-weak",
        color: "text-accent-blue-strong",
      };
    case "REJECT":
      return {
        leftIcon: "quick_reference_all",
        fill: "background-accent-red-weak",
        color: "text-accent-red-strong",
      };
    case "RESTRICTED":
      return {
        leftIcon: "block",
        fill: "background-accent-neutral-weak",
        color: "icon-weak",
      };
    case "REVIEW":
    default:
      return {
        leftIcon: "quick_reference_all",
        fill: "background-accent-orange-weak",
        color: "text-accent-orange-strong",
      };
  }
}

export type AdjudicationStatus =
  | "REVIEW"
  | "CLEAR"
  | "CUSTOM"
  | "NOT_SET"
  | "CRITERIA_NOT_MET";

export function AdjudicationStatusTag(
  status: AdjudicationStatus
): React.ReactElement {
  const statusConfig = {
    REVIEW: {
      fill: "background-accent-orange-weak",
      color: "text-accent-orange-strong",
      label: "Review",
    },
    CLEAR: {
      fill: "background-accent-blue-weak",
      color: "text-accent-blue-strong",
      label: "Clear",
    },
    CUSTOM: {
      fill: "background-accent-purple-weak",
      color: "text-accent-purple-strong",
      label: "Custom",
    },
    NOT_SET: {
      fill: "background-accent-neutral-weak",
      color: "text-base",
      label: "Not Set",
    },
    CRITERIA_NOT_MET: {
      fill: "background-accent-red-weak",
      color: "text-accent-red-strong",
      label: "Not Met",
    },
  };

  const config = statusConfig[status];

  return (
    <StatusTag
      fill={config.fill as CascadiaColor}
      color={config.color as CascadiaColor}
      statusInfoIcon={false}
    >
      {config.label}
    </StatusTag>
  );
}
